<template>
  <div class="home-page">
    <div class="content_retix">
      <div class="container-retix">

        <!-- En-tête -->
        <div class="home-header">
          <div class="home-header__left">
            <h1 class="home-title">Mes études</h1>
            <span class="home-count">{{ drafts.length }} étude{{ drafts.length !== 1 ? 's' : '' }}</span>
          </div>
        </div>

        <!-- Filtres et recherche au-dessus du tableau (12/10/2026) ; utilisateur : seulement si la liste en compte plusieurs -->
        <div v-if="drafts.length" class="liste-outils">
          <filtre-bouton v-if="utilisateurs.length > 1" v-model="filtreUtilisateur" nom="utilisateur" libelle="Utilisateur"
            tous="Tous les utilisateurs" :options="utilisateurs.map(u => ({ value: u, label: u }))" />
          <filtre-bouton v-model="filtreStatut" nom="statut" libelle="État" tous="Tous les états"
            :options="[{ value: 'DRAFT', label: 'Brouillons' }, { value: 'COMPLETED', label: 'Terminées' }]" />
          <input v-model="searchInput" type="search" placeholder="Rechercher par nom de projet..." class="home-search__input"
            aria-label="Rechercher par nom de projet" />
        </div>

        <!-- État vide -->
        <div v-if="drafts.length === 0 && !isLoading" class="home-empty">
          <div class="home-empty__icon">📋</div>
          <p class="home-empty__text">Aucune étude pour le moment.</p>
          <router-link to="/nouvelle-etude" class="home-empty__cta">
            Créer ma première étude →
          </router-link>
        </div>

        <!-- Tableau des études -->
        <div v-else class="studies-table">
          <!-- En-tête colonnes -->
          <div class="studies-table__header">
            <div class="col-name">
              <button class="sort-btn" @click="toggleSort('name')">
                Nom du projet
                <span class="sort-icon">{{ sortField === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </button>
            </div>
            <div class="col-user">
              <button class="sort-btn" @click="toggleSort('user')">
                Utilisateur
                <span class="sort-icon">{{ sortField === 'user' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </button>
            </div>
            <div class="col-date">
              <button class="sort-btn" @click="toggleSort('date')">
                Dernière modif.
                <span class="sort-icon">{{ sortField === 'date' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </button>
            </div>
            <div class="col-status">
              <button class="sort-btn" @click="toggleSort('status')">
                Statut
                <span class="sort-icon">{{ sortField === 'status' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </button>
            </div>
            <div class="col-actions">Actions</div>
          </div>

          <!-- Lignes -->
          <div
            v-for="draft in filteredSortedDrafts"
            :key="draft.id"
            class="study-row"
            :class="draft.status === 'COMPLETED' ? 'study-row--done' : 'study-row--draft'"
          >
            <div class="col-name">
              <span class="study-name">{{ draft.projectName || '(Sans nom)' }}</span>
            </div>
            <div class="col-user">{{ draft.userName || '—' }}</div>
            <div class="col-date study-date">{{ formatDraftDate(draft.updatedAt) }}</div>
            <div class="col-status">
              <span class="status-badge" :class="draft.status === 'COMPLETED' ? 'status-badge--done' : 'status-badge--draft'">
                {{ draft.status === 'COMPLETED' ? 'Terminée' : 'Brouillon' }}
              </span>
            </div>
            <div class="col-actions">
              <!-- Confirmation suppression inline -->
              <template v-if="confirmDeleteId === draft.id">
                <span class="confirm-text">Confirmer ?</span>
                <button class="btn-action btn-action--danger" @click="deleteDraft(draft.id)">
                  Oui
                </button>
                <button class="btn-action btn-action--ghost" @click="confirmDeleteId = null">
                  Non
                </button>
              </template>
              <template v-else>
                <button class="btn-action btn-action--primary" @click="modifierEtude(draft)">
                  Modifier
                </button>
                <button
                  v-if="draft.status === 'COMPLETED'"
                  class="btn-action btn-action--secondary"
                  @click="visualiserEtude(draft)"
                >
                  Visualiser
                </button>
                <button class="btn-action btn-action--ghost" @click="confirmDeleteId = draft.id">
                  Supprimer
                </button>
              </template>
            </div>
          </div>

          <!-- Aucun résultat de recherche -->
          <div v-if="filteredSortedDrafts.length === 0" class="studies-table__empty-search">
            Aucune étude ne correspond {{ searchInput ? 'à "' + searchInput + '"' : 'aux filtres' }}.
          </div>
        </div>

      </div>
    </div>
    <ModalError :message="errorMessage" :title="errorTitle" />
  </div>
</template>

<script>
import axios from 'axios';
import ModalError from '../components/ModalError';
import FiltreBouton from '../components/FiltreBouton.vue';
import { authenticationService } from '../services/authentication';
import { conf } from '@/config/config';

export default {
  components: { ModalError, FiltreBouton },
  data() {
    return {
      drafts: [],
      searchInput: '',
      // filtres (demande du 08/10/2026) ; utilisateur : seulement quand la liste en compte plusieurs (admin, resp d'agence)
      filtreStatut: '',
      filtreUtilisateur: '',
      sortField: 'date',
      sortDir: 'desc',
      confirmDeleteId: null,
      errorMessage: '',
      errorTitle: '',
      isLoading: false,
    };
  },
  computed: {
    utilisateurs() {
      return [...new Set(this.drafts.map((d) => d.userName).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
    },
    filteredSortedDrafts() {
      let list = this.drafts;
      if (this.searchInput) {
        const q = this.searchInput.toLowerCase();
        list = list.filter((d) => (d.projectName || '').toLowerCase().includes(q));
      }
      if (this.filtreStatut) list = list.filter((d) => d.status === this.filtreStatut);
      if (this.filtreUtilisateur) list = list.filter((d) => d.userName === this.filtreUtilisateur);
      return list.slice().sort((a, b) => {
        const dir = this.sortDir === 'asc' ? 1 : -1;
        if (this.sortField === 'date') {
          return (new Date(a.updatedAt) - new Date(b.updatedAt)) * dir;
        }
        const champ = { name: 'projectName', user: 'userName', status: 'status' }[this.sortField];
        const na = (a[champ] || '').toLowerCase();
        const nb = (b[champ] || '').toLowerCase();
        return na < nb ? -dir : na > nb ? dir : 0;
      });
    },
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDir = 'asc';
      }
    },
    modifierEtude(draft) {
      this.$store.commit('wizard/RESET');
      const query = { draftId: draft.id };
      if (draft.status === 'COMPLETED') query.mode = 'edit';
      this.$router.push({ name: 'nouvelle-etude', query });
    },
    visualiserEtude(draft) {
      this.$store.commit('wizard/RESET');
      this.$router.push({ name: 'nouvelle-etude', query: { draftId: draft.id } });
    },
    async deleteDraft(draftId) {
      try {
        const token = authenticationService.status.jwtToken;
        const response = await axios.delete(conf.API_URL + '/wizard/draft/' + draftId, {
          headers: { Authorization: 'Bearer ' + token },
        });
        // refus du serveur : réponse HTTP 200 avec statusCode 404 dans le corps (B24) — la ligne reste affichée
        if (response.data && response.data.statusCode && response.data.statusCode !== 200) {
          throw new Error(response.data.message || 'refus');
        }
        this.drafts = this.drafts.filter((d) => d.id !== draftId);
        this.confirmDeleteId = null;
      } catch (e) {
        this.errorTitle = 'Erreur';
        this.errorMessage = 'Impossible de supprimer cette étude.';
        this.$bvModal.show('error-modal');
      }
    },
    formatDraftDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    },
    async loadDrafts() {
      this.isLoading = true;
      try {
        const token = authenticationService.status.jwtToken;
        const response = await axios.get(conf.API_URL + '/wizard/draft', {
          headers: { Authorization: 'Bearer ' + token },
        });
        if (response.data && response.data.data) {
          this.drafts = response.data.data;
        }
      } catch (e) {
        // pas de drafts ou erreur réseau — liste vide
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.loadDrafts();
  },
};
</script>

<style scoped>
.home-page {
  font-family: 'Raleway', sans-serif;
  min-height: calc(100vh - 80px);
  background: #f5f5f5;
}

.home-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 0 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.home-header__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.home-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
}

.home-count {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
}

.liste-outils {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}
.liste-outils .home-search__input { margin-left: auto; }

.home-search__input {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  width: 260px;
  outline: none;
  transition: border-color 0.15s;
}

.home-search__input:focus {
  border-color: #f5c518;
}

/* État vide */
.home-empty {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 16px;
  margin-top: 8px;
}

.home-empty__icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.home-empty__text {
  font-size: 16px;
  color: #888;
  margin-bottom: 20px;
}

.home-empty__cta {
  display: inline-block;
  padding: 12px 28px;
  background: #f5c518;
  color: #222;
  font-weight: 700;
  font-size: 14px;
  border-radius: 24px;
  text-decoration: none;
  transition: background 0.15s;
}

.home-empty__cta:hover {
  background: #e6b800;
}

/* Tableau */
.studies-table {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-top: 8px;
}

.studies-table__header {
  display: grid;
  grid-template-columns: 1fr 160px 140px 120px 260px;
  padding: 0 24px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  height: 44px;
  align-items: center;
}

.sort-btn {
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  font-family: 'Raleway', sans-serif;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
}

.sort-btn:hover {
  color: #555;
}

.sort-icon {
  font-size: 11px;
}

.col-status,
.col-actions,
.col-user {
  font-size: 12px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.study-row .col-user {
  font-size: 13px;
  font-weight: 400;
  color: #555;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.study-row {
  display: grid;
  grid-template-columns: 1fr 160px 140px 120px 260px;
  padding: 0 24px;
  height: 60px;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
}

.study-row:last-child {
  border-bottom: none;
}

.study-row:hover {
  background: #fafafa;
}

.study-row--draft {
  border-left: 3px solid #f5c518;
}

.study-row--done {
  border-left: 3px solid #4caf50;
}

.study-name {
  font-size: 14px;
  font-weight: 600;
  color: #222;
}

.study-date {
  font-size: 13px;
  color: #888;
}

.status-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 12px;
}

.status-badge--draft {
  background: #fff8e1;
  color: #f9a825;
}

.status-badge--done {
  background: #e8f5e9;
  color: #388e3c;
}

/* Actions */
.col-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: none;
  letter-spacing: 0;
}

.btn-action {
  padding: 6px 14px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  border: none;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}

.btn-action--primary {
  background: #f5c518;
  color: #222;
}

.btn-action--primary:hover {
  background: #e6b800;
}

.btn-action--secondary {
  background: #e3f2fd;
  color: #1565c0;
}

.btn-action--secondary:hover {
  background: #bbdefb;
}

.btn-action--ghost {
  background: #f5f5f5;
  color: #666;
}

.btn-action--ghost:hover {
  background: #eee;
}

.btn-action--danger {
  background: #fce4ec;
  color: #c62828;
}

.btn-action--danger:hover {
  background: #f8bbd0;
}

.confirm-text {
  font-size: 13px;
  color: #c62828;
  font-weight: 600;
}

.studies-table__empty-search {
  padding: 32px 24px;
  text-align: center;
  color: #aaa;
  font-size: 14px;
}
</style>
